"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { getAccessToken } from "../lib/auth";
import { deleteCookie, setCookie } from "../lib/cookies";
import { useRouter } from "next/navigation";
import { registerPushNotifications, installPushSubscriptionLifecycleListeners } from "../lib/pwa";
import { isAdminPwaContext } from "../pwa/pwaApp";

const AuthContext = createContext(null);

function roleDash(role) {
  if (role === "director") return "/director";
  if (role === "hr") return "/hr";
  if (role === "super_admin") return "/super-admin";
  return "/employee";
}

export function AuthProvider({ children }) {
  const router = useRouter();
  const [user, setUser] = useState(null);
  const [mustResetPassword, setMustResetPassword] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const token = getAccessToken();
      if (!token) {
        setUser(null);
        setMustResetPassword(false);
        return null;
      }
      
      const res = await api.get("/me", {
        headers: { Authorization: `Bearer ${token}` }
      });
      const u = res?.data?.data?.user || res?.data?.user || null;
      const mrp =
        res?.data?.data?.must_reset_password ??
        res?.data?.must_reset_password ??
        false;
      setUser(u);
      setMustResetPassword(!!mrp);
      return u;
    } catch (error) {
      // Only clear user if it's an auth error, not network errors
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        setUser(null);
        setMustResetPassword(false);
      }
      return null;
    }
  }, []);

  useEffect(() => {
    (async () => {
      await fetchMe();
      setLoading(false);
    })();
  }, [fetchMe]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    installPushSubscriptionLifecycleListeners();
  }, []);

  // Sync push only when permission is already granted (no permission dialog — Chrome requires a user gesture).
  useEffect(() => {
    if (!user) return;
    registerPushNotifications().catch(() => {});
  }, [user]);

  const login = useCallback(
    async (email, password) => {
      const res = await api.post("/login", { email, password });
      const data = res?.data?.data;
      if (!data?.access_token || !data?.user) throw new Error("Invalid login response");

      setCookie("access_token", data.access_token, 365, data.user.role);
      setCookie("refresh_token", data.refresh_token, 3650, data.user.role);
      setCookie("role", data.user.role, 3650, data.user.role);

      setUser(data.user);
      setMustResetPassword(!!data.must_reset_password);
      
      if (data.must_reset_password) {
        router.replace("/reset-password");
      } else if (isAdminPwaContext()) {
        if (data.user.role !== "super_admin") {
          throw new Error("This app is for Admin Console only. Use the Attendance app for your account.");
        }
        router.replace("/super-admin");
      } else {
        router.replace(roleDash(data.user.role));
      }
      return data;
    },
    [router]
  );

  const signup = useCallback(
    async (name, email, password, role = "director") => {
      const res = await api.post("/signup", { name, email, password, role });
      const data = res?.data?.data;
      if (!data?.access_token || !data?.user) throw new Error("Invalid signup response");

      setCookie("access_token", data.access_token, 365, data.user.role);
      setCookie("refresh_token", data.refresh_token, 3650, data.user.role);
      setCookie("role", data.user.role, 3650, data.user.role);
      setUser(data.user);
      setMustResetPassword(!!data.must_reset_password);
      
      router.replace(roleDash(data.user.role));
      return data;
    },
    [router]
  );

  const logout = useCallback(() => {
    deleteCookie("access_token");
    deleteCookie("refresh_token");
    deleteCookie("role");
    setUser(null);
    setMustResetPassword(false);
    router.replace(isAdminPwaContext() ? "/login?mode=pwa&app=admin" : "/login");
  }, [router]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onForceLogout = () => logout();
    window.addEventListener("auth:force-logout", onForceLogout);
    return () => window.removeEventListener("auth:force-logout", onForceLogout);
  }, [logout]);

  const value = useMemo(
    () => ({
      user,
      mustResetPassword,
      loading,
      login,
      signup,
      logout,
      refreshMe: fetchMe
    }),
    [user, mustResetPassword, loading, login, signup, logout, fetchMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

